/* eslint-disable no-restricted-imports */
import { Box, Typography } from '@mui/material';
import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { ProductRibbons } from '@/components/blocks/Ribbons';
import { ProductType } from '@/data/types/Product';
import { getImage } from '@/utils/getImage';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { formatPrice } from 'integration/utils/formatPrice';
import {
	sponsoredProductImageSX,
	sponsoredProductInfoSX,
	sponsoredProductSX,
} from '@/components/blocks/DpCatalogEntrySearch/styles/container';
import {
	sponsoredPriceOldSX,
	sponsoredPriceSX,
	sponsoredPricesSX,
	sponsoredProductTitleSX,
} from '@/components/blocks/DpCatalogEntrySearch/styles/typography';
import {
	repurchaseProductCouponPriceSX,
	repurchaseProductSizeSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/typography';
import { applyRepurchaseDiscount } from '@/data/Content/RepurchaseCouponState';
import { usePersonalizationPercentage } from '@/data/Content/RepurchaseCoupon';
import { combineSX } from '@/utils/combineSX';

type Props = {
	product: ProductType;
	sizeLabel?: string;
	excluded?: boolean;
};

const getCouponPrices = (
	{ price, priceWithoutOffer, shouldOffer }: ReturnType<typeof calculateProductPrices>,
	excluded: boolean,
	percentage?: number
) => {
	const basePrice = shouldOffer ? price : priceWithoutOffer;
	const couponActive = !!percentage && !excluded && basePrice > 0;
	return {
		couponActive,
		finalPrice: couponActive ? applyRepurchaseDiscount(basePrice, percentage) : basePrice,
		oldPrice: couponActive ? basePrice : priceWithoutOffer,
		showOldPrice: couponActive || shouldOffer,
	};
};

export const RepurchaseProductCard = ({ product, sizeLabel, excluded = false }: Props) => {
	const prices = calculateProductPrices({
		list: product.productPrice?.list ?? 0,
		offer: product.productPrice?.offer ?? 0,
		ribbons: product.ribbons ?? [],
	});
	const sortedRibbons = mergeRibbonsForProduct(product.ribbons ?? []);
	const percentage = usePersonalizationPercentage();
	const { couponActive, finalPrice, oldPrice, showOldPrice } = getCouponPrices(
		prices,
		excluded,
		percentage
	);

	return (
		<Linkable href={product.seo?.href} sx={sponsoredProductSX} aria-label={product.name}>
			<Box sx={sponsoredProductImageSX}>
				<Img src={getImage(product.thumbnail)} alt={product.name} />
				<ProductRibbons ribbons={sortedRibbons} />
			</Box>
			<Box sx={sponsoredProductInfoSX}>
				<Typography component="h3" sx={sponsoredProductTitleSX}>
					{product.name}
				</Typography>
				{sizeLabel ? (
					<Typography component="span" sx={repurchaseProductSizeSX}>
						{sizeLabel}
					</Typography>
				) : null}
				<Box sx={sponsoredPricesSX}>
					<Typography
						component="span"
						sx={combineSX([sponsoredPriceSX, couponActive ? repurchaseProductCouponPriceSX : {}])}
					>
						{formatPrice(finalPrice, 'mxn')}
					</Typography>
					{showOldPrice ? (
						<Typography component="span" sx={sponsoredPriceOldSX}>
							{formatPrice(oldPrice, 'mxn')}
						</Typography>
					) : null}
				</Box>
			</Box>
		</Linkable>
	);
};
